import { ReactNode } from 'react';

import {
  ShieldAlertIcon,
  ShieldCheckIcon,
  ShieldIcon,
  ShieldOffIcon,
  ShieldXIcon,
  WebhookIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import {
  AuthEventType,
  AuthStepType,
  AuthTerminalCause,
  AuthValidationMethod,
  AuthValidationResult,
  DebugStep,
} from '@/shared/types/tryIt';

import {
  DetailRowSpec,
  ExpandableRow,
  NewTabStepLink,
  PlainRow,
  TooltipStepLink,
} from './DetailsRow';
import { OutcomeBadge, WebhookContextCard } from './StepRows';
import { useGoToAuthQuestion } from '../../../hooks/useGoToAuthQuestion';
import { useGoToWebhookPage, useWebhookName } from '../../../hooks/useWebhookLink';
import { ConversationTurn } from '../../../types/conversation';

type Tone = 'neutral' | 'success' | 'warning' | 'destructive';

const EXIT_TRANSLATION_KEY: Partial<Record<AuthTerminalCause, string>> = {
  [AuthTerminalCause.MAX_ATTEMPTS]: 'try_it.auth.exit_max_attempts',
  [AuthTerminalCause.TIMEOUT]: 'try_it.auth.exit_timeout',
  [AuthTerminalCause.SYSTEM_FAILURE]: 'try_it.auth.exit_system_failure',
  [AuthTerminalCause.LLM_EXIT]: 'try_it.auth.exit_llm_exit',
};

const RESULT_TRANSLATION_KEY: Partial<Record<AuthValidationResult, string>> = {
  [AuthValidationResult.VALID]: 'try_it.auth.result.valid',
  [AuthValidationResult.INVALID]: 'try_it.auth.result.invalid',
  [AuthValidationResult.ERROR]: 'try_it.auth.result.error',
};

const STEP_TRANSLATION_KEY: Partial<Record<AuthStepType, string>> = {
  [AuthStepType.IDENTIFICATION]: 'try_it.auth.identification',
  [AuthStepType.VERIFICATION]: 'try_it.auth.verification',
};

const VALIDATION_METHOD_TRANSLATION_KEY: Partial<Record<AuthValidationMethod, string>> = {
  [AuthValidationMethod.EXACT_MATCH]: 'try_it.auth.method_label.exact_match',
  [AuthValidationMethod.FUZZY_MATCH]: 'try_it.auth.method_label.fuzzy_match',
  [AuthValidationMethod.CONTAINS]: 'try_it.auth.method_label.contains',
  [AuthValidationMethod.STARTS_WITH]: 'try_it.auth.method_label.starts_with',
  [AuthValidationMethod.ENDS_WITH]: 'try_it.auth.method_label.ends_with',
  [AuthValidationMethod.GUIDELINE]: 'try_it.auth.method_label.guideline',
  [AuthValidationMethod.WEBHOOK]: 'try_it.auth.method_label.webhook',
  [AuthValidationMethod.FORMAT]: 'try_it.auth.method_label.format',
};

const GROUND_TRUTH_LABEL_KEY: Partial<Record<AuthValidationMethod, string>> = {
  [AuthValidationMethod.CONTAINS]: 'try_it.auth.ground_truth_label.contained_in',
  [AuthValidationMethod.STARTS_WITH]: 'try_it.auth.ground_truth_label.at_start_of',
  [AuthValidationMethod.ENDS_WITH]: 'try_it.auth.ground_truth_label.at_end_of',
};

const toneClassName: Record<Tone, string> = {
  neutral: 'text-foreground-muted',
  success: 'text-text-success-firm',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

const toneFlavour: Record<Tone, 'successful' | 'warning' | 'destructive' | 'neutral'> = {
  neutral: 'neutral',
  success: 'successful',
  warning: 'warning',
  destructive: 'destructive',
};

const toneFromResult = (result?: AuthValidationResult): Tone => {
  if (result === AuthValidationResult.VALID) {
    return 'success';
  }
  if (result === AuthValidationResult.INVALID) {
    return 'warning';
  }
  if (result === AuthValidationResult.ERROR) {
    return 'destructive';
  }
  return 'neutral';
};

const toneFromCause = (cause?: AuthTerminalCause): Tone => {
  if (cause === AuthTerminalCause.THRESHOLD_MET) {
    return 'success';
  }
  if (cause === AuthTerminalCause.LLM_EXIT) {
    return 'warning';
  }
  return 'destructive';
};

const getTone = (step: DebugStep): Tone => {
  const auth = step.auth;
  if (auth?.event === AuthEventType.AUTH_VALIDATE_INPUT) {
    return toneFromResult(auth.validation_result);
  }
  if (auth?.event === AuthEventType.AUTH_PRE_ENTER_WEBHOOK) {
    return auth.validation_result === AuthValidationResult.ERROR ? 'destructive' : 'neutral';
  }
  if (auth?.event === AuthEventType.AUTH_EXIT) {
    return toneFromCause(auth.terminal_cause);
  }
  if (auth?.event === AuthEventType.AUTH_VALIDATOR_SETUP_ERROR) {
    return 'destructive';
  }
  return 'neutral';
};

const AuthIcon = ({ step }: { step: DebugStep }) => {
  const auth = step.auth;
  const props = { size: 16, className: cn('shrink-0', toneClassName[getTone(step)]) };

  switch (auth?.event) {
    case AuthEventType.AUTH_PRE_ENTER_WEBHOOK:
      return <WebhookIcon {...props} />;
    case AuthEventType.AUTH_VALIDATE_INPUT:
      return step.webhook_id ? <WebhookIcon {...props} /> : <ShieldIcon {...props} />;
    case AuthEventType.AUTH_EXIT:
      if (auth.terminal_cause === AuthTerminalCause.THRESHOLD_MET) {
        return <ShieldCheckIcon {...props} />;
      }
      if (auth.terminal_cause === AuthTerminalCause.LLM_EXIT) {
        return <ShieldOffIcon {...props} />;
      }
      return <ShieldXIcon {...props} />;
    case AuthEventType.AUTH_VALIDATOR_SETUP_ERROR:
      return <ShieldAlertIcon {...props} />;
    default:
      return <ShieldIcon {...props} />;
  }
};

const CodePill = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center rounded bg-bg-muted-50 px-1.5 py-0.5 font-mono text-xs text-foreground">
    {children}
  </span>
);

const WebhookLink = ({ webhookId }: { webhookId: string }) => {
  const { t } = useTranslation();
  const name = useWebhookName(webhookId);
  const link = useGoToWebhookPage(webhookId);

  return (
    <NewTabStepLink to={link?.to ?? null} tooltip={t('try_it.details.go_to_webhook')}>
      {name || webhookId}
    </NewTabStepLink>
  );
};

const AuthQuestionLink = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const link = useGoToAuthQuestion({
    actionId: step.action_id,
    step: step.auth?.step,
    questionKey: step.auth?.question_key,
  });

  return (
    <TooltipStepLink link={link ?? null} tooltip={t('try_it.go_to_dialog_node')}>
      {step.auth?.question_key}
    </TooltipStepLink>
  );
};

const AuthStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const auth = step.auth;
  const stepKey = auth?.step ? STEP_TRANSLATION_KEY[auth.step] : '';
  const stepLabel = stepKey ? t(stepKey) : '';

  const content = (): ReactNode => {
    switch (auth?.event) {
      case AuthEventType.AUTH_STEP_ENTERED:
        return t('try_it.auth.step_entered', { 0: stepLabel });
      case AuthEventType.AUTH_PRE_ENTER_WEBHOOK:
        return (
          <>
            {t('try_it.auth.pre_enter_webhook_prefix')}{' '}
            {step.webhook_id ? <WebhookLink webhookId={step.webhook_id} /> : null}
          </>
        );
      case AuthEventType.AUTH_VALIDATE_INPUT:
        return (
          <>
            {t('try_it.auth.validated_input_prefix')} <AuthQuestionLink step={step} />
            {': '}
            <CodePill>{auth.input_value}</CodePill>
            {step.webhook_id ? (
              <>
                {' '}
                {t('try_it.auth.via')} <WebhookLink webhookId={step.webhook_id} />
              </>
            ) : null}
          </>
        );
      case AuthEventType.AUTH_EXIT: {
        if (auth.terminal_cause === AuthTerminalCause.THRESHOLD_MET) {
          return t('try_it.auth.step_completed', { 0: stepLabel });
        }
        const exitKey = auth.terminal_cause ? EXIT_TRANSLATION_KEY[auth.terminal_cause] : undefined;
        return exitKey ? t(exitKey) : null;
      }
      case AuthEventType.AUTH_VALIDATOR_SETUP_ERROR:
        return (
          <>
            {t('try_it.auth.validator_setup_error')} <AuthQuestionLink step={step} />
            {auth.missing_context_variable ? (
              <>
                {' — '}
                {t('try_it.auth.missing_context_variable')}{' '}
                <CodePill>{auth.missing_context_variable}</CodePill>{' '}
                {t('try_it.auth.missing_context_variable_suffix')}
              </>
            ) : null}
            {auth.match_size !== undefined && auth.ground_truth_length !== undefined ? (
              <>
                {' — '}
                {t('try_it.auth.match_size_exceeds_ground_truth', {
                  0: auth.match_size,
                  1: auth.ground_truth_length,
                })}
              </>
            ) : null}
          </>
        );
      default:
        return null;
    }
  };

  return <PlainRow>{content()}</PlainRow>;
};

const callDuration = (step: DebugStep): number | undefined =>
  step.webhook_id || step.auth?.event === AuthEventType.AUTH_PRE_ENTER_WEBHOOK
    ? step.duration
    : undefined;

const AuthStatusPills = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const auth = step.auth;
  const stepKey = auth?.step ? STEP_TRANSLATION_KEY[auth.step] : undefined;
  const methodKey =
    (auth?.validation_method && VALIDATION_METHOD_TRANSLATION_KEY[auth.validation_method]) ||
    (auth?.event === AuthEventType.AUTH_VALIDATE_INPUT
      ? 'try_it.auth.method_label.unknown'
      : undefined);
  const resultKey = auth?.validation_result
    ? RESULT_TRANSLATION_KEY[auth.validation_result]
    : undefined;
  const duration = callDuration(step);

  const methodLabel = methodKey ? t(methodKey) : undefined;
  const resultLabel = resultKey ? t(resultKey) : undefined;
  const outcome =
    methodLabel && resultLabel
      ? t('try_it.auth.heading_method_result', { 0: methodLabel, 1: resultLabel })
      : (methodLabel ?? resultLabel);

  if (!stepKey && !outcome && duration === undefined) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {stepKey && <OutcomeBadge flavour="informational">{t(stepKey)}</OutcomeBadge>}
      {outcome && <OutcomeBadge flavour={toneFlavour[getTone(step)]}>{outcome}</OutcomeBadge>}
      {duration !== undefined && (
        <OutcomeBadge className="border-border">
          {t('try_it.details.execution_time', { 0: duration })}
        </OutcomeBadge>
      )}
    </div>
  );
};

const validationFields = (step: DebugStep, t: ReturnType<typeof useTranslation>['t']) => {
  const auth = step.auth;
  if (auth?.event !== AuthEventType.AUTH_VALIDATE_INPUT) {
    return [];
  }

  const fields: { label: string; value: string }[] = [];
  if (auth.ground_truth) {
    const groundTruthLabelKey = auth.validation_method
      ? GROUND_TRUTH_LABEL_KEY[auth.validation_method]
      : undefined;
    fields.push({
      label: t(groundTruthLabelKey ?? 'try_it.auth.expected_value'),
      value: auth.ground_truth,
    });
  }
  if (auth.fuzzy_score !== undefined) {
    fields.push({
      label: t('try_it.auth.score'),
      value: t('try_it.auth.fuzzy_score_value', { 0: auth.fuzzy_score }),
    });
  }
  if (auth.match_size) {
    fields.push({ label: t('try_it.auth.match_size'), value: String(auth.match_size) });
  }

  return fields;
};

const AuthValidationFields = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const fields = validationFields(step, t);

  if (fields.length === 0) {
    return null;
  }

  return (
    <ul className="ml-4 list-disc text-sm leading-5 break-words text-foreground-muted">
      {fields.map((field) => (
        <li key={field.label}>
          {field.label}: {field.value}
        </li>
      ))}
    </ul>
  );
};

const EXPANDABLE_EVENTS = new Set([
  AuthEventType.AUTH_PRE_ENTER_WEBHOOK,
  AuthEventType.AUTH_VALIDATE_INPUT,
]);

const hasAuthDetail = (step: DebugStep): boolean => {
  const auth = step.auth;
  if (!auth || !EXPANDABLE_EVENTS.has(auth.event)) {
    return false;
  }

  return (
    auth.step !== undefined ||
    auth.validation_method !== undefined ||
    auth.validation_result !== undefined ||
    callDuration(step) !== undefined ||
    auth.ground_truth !== undefined ||
    auth.fuzzy_score !== undefined ||
    Boolean(auth.match_size) ||
    step.webhook_error !== undefined ||
    Object.keys(step.context_changes ?? {}).length > 0
  );
};

const AuthStepRow = ({ turn, step }: { turn: ConversationTurn; step: DebugStep }) => {
  const { t } = useTranslation();

  return (
    <ExpandableRow
      head={<AuthStepHead step={step} />}
      label={t('try_it.details.show_call_details')}
      detail={
        hasAuthDetail(step) && (
          <div className="flex flex-col items-start gap-2 self-stretch pb-2">
            <AuthStatusPills step={step} />
            <AuthValidationFields step={step} />
            <WebhookContextCard
              step={step}
              steps={turn.metadata.steps ?? []}
              context={turn.metadata.context}
              subject={step.auth?.question_key}
            />
          </div>
        )
      }
    />
  );
};

export const buildAuthRow = (
  turn: ConversationTurn,
  step: DebugStep,
  key: string
): DetailRowSpec => ({
  key,
  icon: <AuthIcon step={step} />,
  head: <AuthStepRow turn={turn} step={step} />,
});
